import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Trans, withNamespaces } from 'react-i18next';

import { Div } from 'legacyComponents/styled/lib';
import { media } from 'legacyComponents/styled/styleUtils';

import { InputWrapper } from 'libs/forms/styles';
import { Editor } from 'libs/editor';
import { Validation } from 'libs/forms/views/validation';
import { EditorFieldStore } from 'libs/forms/fieldStores';
import { Field } from 'libs/forms/views/field';

import { withConditional } from 'legacyComponents/hoc/conditional';

const ValidationIndicator = withConditional(Validation);

const TextAreaWrapper = styled(InputWrapper)`
  min-height: 80px;
  height: initial;
  padding: 12px 10px;
  position: relative;
  margin: ${props => `${props.theme.spacing.small} 0 41px 0`};
  ${media.xs`
    font-size: 16px;
  `};
`;

const StyledEditor = styled(Editor)`
  width: calc(100% - 20px);
  color: ${props => props.theme.color.wfGrey900};
  line-height: 1.6;
  padding: 10px 10px;
  min-height: 80px;
  resize: none;
  cursor: ${props => (props.posting ? 'progress !important' : 'inherit')};
  background-color: transparent;
  color: red;
`;

const NoteWrapper = styled.div`
  position: absolute;
  bottom: -25px;
  left: 0;
  right: 0;
  border-right: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.wfBlack}`
      : `1px solid ${props.theme.color.wfGrey400}`};
  border-bottom: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.wfBlack}`
      : `1px solid ${props.theme.color.wfGrey400}`};
  border-left: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.wfBlack}`
      : `1px solid ${props.theme.color.wfGrey400}`};
  background-color: ${props => props.theme.color.wfWhite};
`;

const BlueNoteWrapper = styled(NoteWrapper)`
  border-right: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.primary}`
      : `1px solid ${props.theme.color.wfGrey200}`};
  border-bottom: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.primary}`
      : `1px solid ${props.theme.color.wfGrey200}`};
  border-left: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.primary}`
      : `1px solid ${props.theme.color.wfGrey200}`};
  box-shadow: ${props =>
    props.focussed ? 'inset 0 1px 2px #c7d8ef, 0 0 0 0.15em #c7d8ef' : 'none'};
`;

const BlueHint = withConditional(BlueNoteWrapper);
const Hint = withConditional(NoteWrapper);

const Note = styled.div`
  padding: 6px;
  background-color: ${props => props.theme.color.wfWhite};
  font-size: 12px;
  text-align: right;
  color: ${props => props.theme.color.wfGrey700};
  opacity: ${props => (props.visible ? '1' : '0')};
  transition: opacity 300ms ease-in-out;
  border-top: 1px dashed ${props => props.theme.color.wfGrey400};
`;

@withNamespaces()
@observer
export class EditorField extends Field {
  static defaultStore = EditorFieldStore;
  componentDidMount() {
    const { fieldStore } = this.props;
    if (fieldStore.autoFocus) {
      this.focusEditor();
    }
  }

  onFocus = () => {
    const { fieldStore } = this.props;
    if (!fieldStore.isFocussed) fieldStore.SetFocus(true);
  };

  onBlur = () => {
    const { fieldStore } = this.props;
    fieldStore.SetFocus(false);
    fieldStore.alert();
  };

  focusEditor = e => {
    const { fieldStore } = this.props;
    if (e && e.target === fieldStore.ref && !fieldStore.editor.readOnly) {
      fieldStore.SetFocus(true);
    }
  };

  render() {
    const { fieldStore, editComponent, noValidationIcon, paragraphSpacing } =
      this.props;
    const {
      noFormattingHint,
      blueBorder,
      noValidation = false,
      dataTestId,
    } = this.props;
    const Tag = editComponent || TextAreaWrapper;
    const NoteTag = blueBorder ? BlueHint : Hint;
    const { editor } = fieldStore;
    return (
      <Div position='relative' data-testid={dataTestId}>
        <Tag
          focussed={fieldStore.isFocussed}
          onClick={this.focusEditor}
          ref={ref => fieldStore.SetRef(ref)}
          responsive
        >
          <StyledEditor
            store={editor}
            id={this.inputId}
            paragraphSpacing={paragraphSpacing}
            onBlur={this.onBlur}
            onFocus={this.onFocus}
            posting={fieldStore.formStore.posting}
          />
        </Tag>
        {!noValidation && (
          <ValidationIndicator
            condition={!editor.readOnly && !noValidationIcon}
            fieldStore={fieldStore}
            handleClick={this.focusAfterDelete}
            positionFixed
          />
        )}
        <NoteTag
          condition={!noFormattingHint}
          focussed={fieldStore.isFocussed}
          blueBorder={blueBorder}
          editComponent={Tag === editComponent}
        >
          <Note
            visible={!fieldStore.isEmpty && fieldStore.isFocussed}
            editComponent={Tag === editComponent}
          >
            <Trans>Select text for formatting options</Trans>
          </Note>
        </NoteTag>
      </Div>
    );
  }
}

EditorField.propTypes = {
  fieldStore: PropTypes.object,
  autoFocus: PropTypes.bool,
  editComponent: PropTypes.object,
  noValidationIconnoFormattingHint: PropTypes.bool,
  noFormattingHint: PropTypes.bool,
  blueBorder: PropTypes.bool,
  placeholder: PropTypes.string,
  paragraphSpacing: PropTypes.string,
  dataTestId: PropTypes.string,
};
